import React from 'react';
import jspdf from 'jspdf';
import html2canvas from 'html2canvas';
import { hexToRgb } from '../helpers/hexToRgb';
import { FORMAT_FULLPAGE } from '../../../cp/components/EventPage/SchedulePdfConstructorPage/constants';

let pdfCtx = null;
const scaleFactor = 2;

function preRenderNode(component, props, ...children) {
    return React.createElement(component, props, ...children);
}

function blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
}

/**
 * @param {object} config config (https://raw.githack.com/MrRio/jsPDF/master/docs/jsPDF.html),
 */
async function initPdfCtx(
    locale = 'ru-RU',
    config = {},
) {
    const options = {
        orientation: config.format === 'a4' && config.orientation ? config.orientation : 'portrait',
        // orientation: config.orientation || 'portrait',
        unit: config.unit || 'px',
        format: config.format || 'a4',
        hotfixes: ['px_scaling'],
        // ...config,
    };

    pdfCtx = new jspdf(options);

    await pdfCtx.setLanguage(locale);

    return pdfCtx;
}

/**
 * 
 * @param {object} html Dom of target element 
 * @param {object} options
 * font - Object with local font data,
 * fileName, 
 * pdfOptions (https://raw.githack.com/MrRio/jsPDF/master/docs/module-html.html#~html)
 */

async function generatePdf(
    html,
    {
        font = '',
        fileName = 'file',
        pdfOptions = {},
        ...restOptions
    }
) {
    const pageSize = pdfCtx.internal.pageSize;

    if (!!restOptions.backgroundColor) {
        const rgbData = hexToRgb(restOptions.backgroundColor);
        pdfCtx.setFillColor(rgbData[0], rgbData[1], rgbData[2]);
        pdfCtx.rect(0, 0, pageSize.width, pageSize.height, 'F');
        
        pdfCtx.internal.events.subscribe('addPage', () => {
            pdfCtx.setFillColor(rgbData[0], rgbData[1], rgbData[2]);
            pdfCtx.rect(0, 0, pageSize.width, pageSize.height, 'F');
        });
    }

    const htmlTargets = restOptions.format.toLowerCase() === FORMAT_FULLPAGE ? [html] : html.querySelectorAll('.pdf-schedule');
    const canvases = [];
    let scale = 1;

    if (restOptions.format !== 'fullpage') {
        scale = pageSize.width > pageSize.height ? pageSize.width / pageSize.height : 1;
    }  

    scale = scale * scaleFactor;

    for await (let target of htmlTargets) {
        const canvas = await html2canvas(target, {
            scale,
            onclone: async function (documentClone) {
                if (!!font?.filepath) {
                    let fontFamily = '';
                    const {list} = await fetch(font?.filepath).then((response) => response.json());
                    const fontsList = list.map((fontSrc) => {
                        const [fontName] = fontSrc.split('/').slice(-1)?.[0].split('.');
                        const [family, weight, style] = fontName.split('-');
                        if (!fontFamily.length) {
                            fontFamily = family;
                        }
    
                        return new FontFace(family, `url(${window.location.origin}${fontSrc})`, {
                            style,
                            weight,
                          });
                    })
    
                    const fonts = await Promise.all(fontsList)
                    const result = await Promise.all(fonts.map((font) => font.load()));
    
                    result.forEach((font) => {
                        documentClone.fonts.add(font);
                    });
                    
                    documentClone.querySelector('body').style.fontFamily = `${fontFamily}, sans-serif`;
                }
            },
        });

        canvases.push(canvas);
    }

    canvases.forEach((canvas, index) => {
        const dataUrl = canvas.toDataURL('image/jpeg', 1);
        const pagesCount = Math.ceil((canvas.height / scaleFactor) / pageSize.height);

        if (index > 0) {
            pdfCtx.addPage();
        }

        pdfCtx.addImage(dataUrl, 'PNG', 0, 0, canvas.width / scaleFactor, canvas.height / scaleFactor);
    
        for (let i = 1; i < pagesCount; i++) {
            pdfCtx.addPage();
            pdfCtx.addImage(dataUrl, 'PNG', 0, -(pageSize.height * i), canvas.width / scaleFactor, canvas.height / scaleFactor);
        }
    })

    pdfOptions.callback(pdfCtx);
}

export  {
    pdfCtx,
    initPdfCtx,
    generatePdf,
    blobToBase64,
    preRenderNode,
};
